import Image from 'next/image';
import { memo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { getShareLinkPageTopCornerClass } from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { getCoverImageObjectPosition } from '~/components/v2/components/showcase/getCoverImageObjectPosition';
import { usePublicShowcaseActiveSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseActiveSubPage';
import { buildCoverFadeGradient, COVER_FADE_DEFAULT } from '~/components/v2/components/showcase/showcaseCoverFade';
import { COVER_WIDTH_DEFAULT } from '~/components/v2/components/showcase/showcaseCoverWidth';
import { SHOWCASE_PAGE_COLUMN_CLASS_NAME } from '~/components/v2/components/showcase/showcasePageColumn';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export const PublicShowcaseCover = memo(() => {
  const { presentationSettings } = useShortIdContext();
  // The surface's cover wrapper: viewers pass the band through bare; the editor's canvas wraps it
  // in its cover-authoring chrome. This component stays surface-blind either way.
  const { renderCover } = usePublicShowcaseContext();
  const { pageBgColor } = usePublicCustomColors();
  const activeSubPage = usePublicShowcaseActiveSubPage();
  // Only the cover IMAGE is per-page and it is INDEPENDENT — a sub-page renders its OWN
  // `settings.coverImage` (absent → no cover, never the root's); the root page renders the
  // link-wide `presentationSettings.coverImage`. `coverWidth`/`coverFade` below stay root-only.
  const coverImage = activeSubPage ? activeSubPage.settings?.coverImage : presentationSettings?.coverImage;
  // Absent coverWidth (links saved before the field existed) must render as the original flush band.
  const coverWidth = presentationSettings?.coverWidth ?? COVER_WIDTH_DEFAULT;
  // Absent coverFade (links saved before the field existed) must render the plain, unfaded cover.
  const coverFade = presentationSettings?.coverFade ?? COVER_FADE_DEFAULT;

  // No banner set — render nothing (the page must look good without a placeholder).
  if (!coverImage?.url) {
    return null;
  }

  const heroBox = (
    <div
      data-testid="PUBLIC_COLLECTION_COVER"
      className={cn(
        'relative aspect-[5.5/1] max-h-[280px] w-full overflow-hidden',
        // Only the inset variant rounds its own top corners (fullWidth/immersive stay flush);
        // the radius follows the link's Corner rounding setting to match the content column.
        coverWidth === 'inset' && getShareLinkPageTopCornerClass(presentationSettings?.thumbnailRadius),
      )}
    >
      {/* Decorative banner only — empty alt keeps it out of the accessibility tree.
          unoptimized: the url is a server-resolved, already-sized S3/imgix asset. */}
      <Image
        src={coverImage.url}
        alt=""
        fill
        unoptimized
        className="object-cover"
        style={{ objectPosition: getCoverImageObjectPosition(coverImage.region) }}
      />
      {coverFade && (
        // Dissolves the image's bottom edge into whatever surface sits below the band — the
        // gradient's target color branches on coverWidth/pageBgColor inside the builder.
        <div
          aria-hidden
          data-testid="PUBLIC_COLLECTION_COVER_FADE"
          className="pointer-events-none absolute inset-0"
          style={{ background: buildCoverFadeGradient({ coverWidth, pageBgColor }) }}
        />
      )}
    </div>
  );

  // The wrapper hugs the band itself (inside the inset column below), so the editor's hover
  // toolbar always sits on the cover's actual bounds.
  const cover = renderCover({ children: heroBox });

  if (coverWidth === 'inset') {
    return (
      // "Inset" pads the cover in from the white column's edges by reusing the column class inside
      // the column: its `min(100% - 80px, var(--share-link-page-max-width))` expression (var hosted
      // on the page root) resolves to "80px narrower than the column, centered", so the column
      // surface stays visible in the side gutters.
      <div className={SHOWCASE_PAGE_COLUMN_CLASS_NAME}>{cover}</div>
    );
  }

  // `fullWidth` and `immersive` both render the bare hero box, which fills whichever slot the
  // page chrome (PublicShowcasePage) mounts it in: fullWidth sits inside the white content
  // column (flush to its edges), while immersive is mounted as a direct child of the full-width
  // scroller, above the column, so it spans the browser edge-to-edge and bleeds under the
  // transparent header. The content column keeps its normal width either way.
  return <>{cover}</>;
});

PublicShowcaseCover.displayName = 'PublicShowcaseCover';
